import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
    Button,
    Card,
    Space,
    Typography,
    Breadcrumb,
    Row,
    Col,
    Popconfirm,
} from "antd";
import {
    EditOutlined,
    HomeOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import CategoryForm from "./CategoryForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import {
    addCategory,
    deleteCategory,
    getCategories,
    updateCategory,
    updateCategoryStatus,
} from "../../store/actions/category.js";
import moment from "moment-timezone";
import CustomToggleSwitch from "../../components/CustomToggleSwitch.js";

const { Title } = Typography;

const Categories = () => {
    document.title = "Categories";
    const [isLoading, setIsLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const dispatch = useDispatch();
    const category = useSelector((state) => state?.category?.list);
    const totalCount = useSelector((state) => state?.category?.totalCount ?? 0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        const fetchCategories = async () => {
            setIsLoading(true);
            try {
                await dispatch(getCategories({ page: currentPage, search: searchText }));
            } catch (error) {
                console.error("Failed to fetch categories:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCategories();
    }, [dispatch, currentPage, searchText]);

    // ─── ADD / UPDATE ────────────────────────────────────────────────────────────
    const handleCreate = async (values) => {
        if (selectedRecord) {
            // ── UPDATE ──────────────────────────────────────────────────────────────
            const changedFields = {};

            if (values.name !== selectedRecord.name)
                changedFields.name = values.name;

            if ((values.ordinal ?? "") !== (selectedRecord.ordinal ?? ""))
                changedFields.ordinal = values.ordinal ?? "";

            if ((values.isForAll ?? false) !== (selectedRecord.isForAll ?? false))
                changedFields.isForAll = values.isForAll ?? false;

            // Nothing changed — skip API call
            if (Object.keys(changedFields).length === 0) {
                setIsModalVisible(false);
                setSelectedRecord(null);
                return;
            }

            await dispatch(
                updateCategory(
                    { id: Number(selectedRecord.id), ...changedFields },
                    false
                )
            );
        } else {
            // ── ADD ──────────────────────────────────────────────────────────────────
            const payload = {
                name: values.name,
                isForAll: values.isForAll ?? false,
                ...(values.ordinal !== undefined &&
                    values.ordinal !== null && { ordinal: Number(values.ordinal) }),
            };
            await dispatch(addCategory(payload, false));
        }

        await dispatch(getCategories(currentPage));
        setIsModalVisible(false);
        setSelectedRecord(null);
    };

    // ─── DELETE ──────────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        await dispatch(deleteCategory(id));
        await dispatch(getCategories(currentPage));
    };

    // ─── STATUS TOGGLE ───────────────────────────────────────────────────────────
    const handleChangeStatus = async (id, status) => {
        await dispatch(updateCategoryStatus(id, status));
        await dispatch(getCategories(currentPage));
    };

    // ─── OPEN EDIT MODAL ─────────────────────────────────────────────────────────
    const handleEdit = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    // ─── CLOSE MODAL ─────────────────────────────────────────────────────────────
    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
    };

    const renderCell = (text) => (text ? text : "N/A");

    let columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: stringSorter("name"),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Ordinal",
            dataIndex: "ordinal",
            key: "ordinal",
            render: renderCell,
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => {
                if (!date) return "-";
                return moment(date, "YYYY-MM-DD HH:mm:ss.SSSSSS").format("DD/MM/YYYY");
            },
            sorter: (a, b) =>
                moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
                    ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
                    : 0,
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => handleEdit(record)}>
                        <EditOutlined />
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    columns = enhanceColumnsWithFilters(columns);

    return (
        <div className="page-content">
            <Breadcrumb style={{ marginBottom: "16px" }}>
                <Breadcrumb.Item href="/">
                    <HomeOutlined />
                </Breadcrumb.Item>
                <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item>Categories</Breadcrumb.Item>
            </Breadcrumb>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={2} style={{ margin: 0 }}>
                                Categories
                            </Title>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setSelectedRecord(null);
                                    setIsModalVisible(true);
                                }}
                            >
                                Add Category
                            </Button>
                        </Col>
                    </Row>

                        <DataTable
                            dataSource={category.map((item) => ({ ...item, key: item.id }))}
                            columns={columns}
                            backendPagination={true}
                            totalRecords={totalCount}
                            page={currentPage}
                            limit={20}
                            onPageChange={setCurrentPage}
                            onSearch={(text) => {
                                setSearchText(text);
                                setCurrentPage(1);
                            }}
                            loading={isLoading}
                        />
                </Space>
            </Card>

            <CategoryForm
                visible={isModalVisible}
                onCreate={handleCreate}
                onCancel={handleCancel}
                record={selectedRecord}
            />
        </div>
    );
};

export default Categories;